import styled from 'styled-components'
import { theme } from '../../../theme/theme'
import { OnClickButton } from '../../buttons/onclick/OnClickButton'
import { Arrow } from '../../icons'
import { ComponentLayout } from '../../layouts/ComponentLayout'
import { BodyLarge, Title2 } from '../../styles/Typography'
import { ScreeningHeader } from '../ScreeningHeader'
import { Stack } from '@mui/material'
import { useSelectLatestScreeningType, useSelectScreeningId, useSelectScreeningStatus } from '../../../state/selectors'
import {
  useGetPreviousQuestionQuery,
  useSubmitScreeningQuery,
  useGetScreeningEndStatusQuery,
} from '../../../api/ester-b2b-api/private/screening/screeningQueries'
import { useScreeningQuestionnaireStore } from '../../../state/store'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isTabletOrMobile } from '../../../utils/devices'
import { ErrorModal } from '../../modals/ErrorModal'
import { SuccessModal } from '../../modals/SuccessModal'
import { useTranslation } from 'react-i18next'
import { ScreeningEndStatus, ScreeningStatus, ScreeningType, TrackingEvents } from '@estercare/ester-shared'
import { useEmitEvent } from '../../../hooks/analytics/useEmitEvent'
import { queryErrorHandlerWithMessage } from '../../../utils/queryErrorHandler'
import { useScreeningEndConfig } from './useScreeningEndConfig'

type Props = {
  disableButton: boolean
  onCloseSuccessModal: () => void
  headerTitle: string
  successModalTitle?: string
  children?: React.ReactNode
}

export const SubmitScreeningTemplate = ({
  disableButton,
  onCloseSuccessModal,
  headerTitle,
  successModalTitle,
  children,
}: Props) => {
  const { t } = useTranslation()
  const { latestScreeningId } = useSelectScreeningId()
  const screeningStatus = useSelectScreeningStatus()
  const screeningType = useSelectLatestScreeningType()
  const { refetch: getPreviousQuestion } = useGetPreviousQuestionQuery(latestScreeningId)
  const { mutate: submitScreening, isPending } = useSubmitScreeningQuery()
  const emitEvent = useEmitEvent()

  const { setCurrentQuestion } = useScreeningQuestionnaireStore()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [endStatus, setEndStatus] = useState<ScreeningEndStatus | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const { refetch: fetchEndStatus } = useGetScreeningEndStatusQuery(latestScreeningId)

  const isScreeningCompleted =
    screeningStatus === ScreeningStatus.FOLLOW_UP_HANDLED || screeningStatus === ScreeningStatus.HANDLED
  const canGoBack = !isScreeningCompleted
  const width = useWindowWidth()
  const isMobileOrTablet = isTabletOrMobile(width)

  const navigate = useNavigate()

  const goToPreviousQuestion = async () => {
    const { data: newQuestion, isSuccess, isError } = await getPreviousQuestion()
    if (isSuccess) {
      setCurrentQuestion(newQuestion)
      navigate('/portal/health-services/screening/questions')
    }
    if (isError) {
      const message = t('employee.screening.submitPreviousError')
      setErrorMessage(message)
    }
  }

  const isFollowUpOrCheckIn = screeningType === ScreeningType.FOLLOW_UP || screeningType === ScreeningType.CHECK_IN
  const isStandard = screeningType === ScreeningType.STANDARD

  const showEndScreen = async () => {
    const { data, isError } = await fetchEndStatus()
    const newEndStatus = data?.endStatus ?? ScreeningEndStatus.DEFAULT
    // The screening is already submitted, so a standard screening never blocks on the end status
    if (isStandard && (isError || newEndStatus !== ScreeningEndStatus.STANDARD_BOOKING_REQUEST)) {
      setShowSuccessModal(true)
      return
    }
    if (isError) {
      const message = t('employee.screening.errorTitle')
      setErrorMessage(message)
      return
    }
    setEndStatus(newEndStatus)
  }

  const submit = () => {
    if (latestScreeningId) {
      submitScreening(
        { screeningId: latestScreeningId },
        {
          onSuccess: async () => {
            emitEvent({
              eventType: TrackingEvents.SUBMIT,
              action: 'submit_screening',
              options: {
                metadata: { screeningType: screeningType ?? '' },
              },
            })
            if (isFollowUpOrCheckIn || isStandard) {
              await showEndScreen()
            } else {
              setShowSuccessModal(true)
            }
          },
          onError: (error) => {
            const message = queryErrorHandlerWithMessage('submit screening', t)(error)
            setErrorMessage(message)
          },
        }
      )
    }
  }

  if (errorMessage) {
    return (
      <ErrorModal
        title={t('employee.screening.errorTitle')}
        text={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
    )
  }

  if (endStatus !== null) {
    return <ScreeningEndScreen endStatus={endStatus} latestScreeningId={latestScreeningId} />
  }

  return (
    <>
      <ScreeningHeader title={headerTitle} goBack={canGoBack ? goToPreviousQuestion : false} progress={100} />
      <ComponentLayout
        noMaxWidth
        paddingTop={isMobileOrTablet ? theme.spacing.xsmall : theme.spacing.xxlarge}
        style={{
          height: '100%',
        }}
      >
        <Section gap={10}>
          <div>
            <StyledTitle2>{t('employee.screening.submitTitle')}</StyledTitle2>
            {children}
          </div>
          <ButtonContainer>
            <OnClickButton
              text={t('employee.screening.submitButton')}
              onClick={submit}
              icon={<Arrow color={theme.color.white} size={15} />}
              disabled={isPending || disableButton}
              loading={isPending}
            />
          </ButtonContainer>
        </Section>
      </ComponentLayout>
      {showSuccessModal && successModalTitle && (
        <SuccessModal title={successModalTitle} onClose={onCloseSuccessModal} />
      )}
    </>
  )
}

const Section = styled(Stack)`
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
  height: 100%;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    & > button {
      min-width: 320px;
    }
  }
`

const StyledTitle2 = styled(Title2)`
  margin-bottom: ${theme.spacing.medium}px;
`

const ScreeningEndScreen = ({
  endStatus,
  latestScreeningId,
}: {
  endStatus: ScreeningEndStatus
  latestScreeningId?: string
}) => {
  const { t } = useTranslation()
  const { text, buttonLabel, onButtonClick } = useScreeningEndConfig(endStatus, latestScreeningId)

  return (
    <ComponentLayout noMaxWidth paddingTop={theme.spacing.xxlarge}>
      <Section gap={10}>
        <div>
          <StyledTitle2>{t('employee.submitScreen.endTitle')}</StyledTitle2>
          <BodyLarge>{text}</BodyLarge>
        </div>
        {buttonLabel && onButtonClick && (
          <ButtonContainer>
            <OnClickButton
              text={buttonLabel}
              onClick={onButtonClick}
              icon={<Arrow color={theme.color.white} size={15} />}
            />
          </ButtonContainer>
        )}
      </Section>
    </ComponentLayout>
  )
}
